import React, { PureComponent, Fragment } from 'react'

import request from 'lib/request'
import Checkbox from 'Components/checkbox'
import safeGet from '@arsenal/arsenal/modules/safeGet'
import Loading from 'Components/loading'

export interface IDealerListProps {
  cityName: string
  carId: number
  defaultValue?: string[]
  error: number // 错误类型 0：无错误，后期根据需要是否需要展示不同的错误信息
  value?: string[]
  dealerLimitCount?: number // 可选经销商数量
  onChange?: (
    checkedValue: string[],
    indexValue: number[],
    err?: string
  ) => void
}

export interface IDealerListState {
  value: string[]
  sortTypeIndex: number
  list: IDealerListItem[]
  tips: string
  loading: boolean
}

export interface IDealerListItem {
  price: string // 价格：10.99万
  address: string
  dealer_full_name: string
  dealer_id: string
  dealer_name: string
  dealer_phone: string
  dealer_type: string // 经销商类型：4s店
  dealer_price: number // 经销商报价：10.99
  lati: number // 经度
  longi: number // 纬度
  official_price: number // 官方指导价：11.59
  sale_region: string // 销售范围：售本市
  show_phone_number: number
  [key: string]: string | number | boolean | Record<string, any>
}

export type TDealerList = IDealerListItem[]

export default class DealerList extends PureComponent<
  IDealerListProps,
  IDealerListState
> {
  static defaultProps: Partial<IDealerListProps> = {
    error: 0,
  }

  static getDerivedStateFromProps(nextProps: IDealerListProps) {
    if ('value' in nextProps) {
      return {
        value: nextProps.value || [],
      }
    }
    return null
  }

  sortTypeList = ['智能推荐', '价格最低']

  defaultDealerList: TDealerList = []

  dealerLimitCount: number

  constructor(props: IDealerListProps) {
    super(props)
    this.state = {
      value: props.value || props.defaultValue || [],
      sortTypeIndex: 0,
      list: [],
      tips: '',
      loading: true,
    }

    this.dealerLimitCount = props.dealerLimitCount ? props.dealerLimitCount : 5
  }

  componentDidMount() {
    this.fetch()
  }

  componentDidUpdate(prevProps: IDealerListProps) {
    const { carId, cityName } = this.props
    if (carId !== prevProps.carId || cityName !== prevProps.cityName) {
      this.fetch()
    }
  }

  fetch() {
    const { carId, cityName } = this.props

    // 数据不合法时不再发起请求，否则会影响loading状态
    if (!carId || !cityName) {
      this.setState({
        loading: false,
      })
      return
    }

    const { sortTypeIndex } = this.state
    const sortType = sortTypeIndex === 0 ? 3 : 1

    this.setState({
      loading: true,
    })

    return request
      .get('/motor/dealer/m/v1/get_dealers_car_info/', {
        car_id: carId,
        city_name: cityName,
        sort_type: sortType, // 3智能推荐 1价格最近 2距离最近
      })
      .then((res: any) => {
        const list: TDealerList = []
        let tips = ''
        res.data.forEach((item: any) => {
          if (item.type === 1035) {
            list.push(item.info)
          } else if (item.type === 1034) {
            tips = safeGet(item, 'info.tips')
          }
        })
        this.setState({
          list,
          tips,
          loading: false,
        })
        this.triggerChange(list.slice(0, 3).map(item => item.dealer_id))
        this.defaultDealerList = list.slice()
      })
  }

  onChangeSortType = (sortTypeIndex: number) => {
    this.setState(
      {
        sortTypeIndex,
      },
      this.fetch
    )
  }

  toggleCheck = (item: IDealerListItem) => {
    const { value } = this.state
    const index = value.indexOf(item.dealer_id)
    const newValue = value.slice()
    let errMsg = ''
    if (index === -1) {
      if (newValue.length < 5) {
        newValue.push(item.dealer_id)
      } else {
        errMsg = 'over_count_limit'
      }
    } else {
      newValue.splice(index, 1)
    }
    this.triggerChange(newValue, errMsg)
  }

  triggerChange(value: string[], err = '') {
    if (!('value' in this.props)) {
      this.setState({ value })
    }
    const { onChange } = this.props
    onChange &&
      onChange(
        value,
        this.defaultDealerList
          .filter(item => value.indexOf(item.dealer_id) > -1)
          .map((_, index) => index),
        err
      )
  }

  _renderDealerList() {
    const { list, value, tips } = this.state

    return (
      <>
        {tips ? <div className="diffent-place-tips">{tips}</div> : null}

        {list.length > 0 ? (
          list.map(item => (
            <div
              className="clearfix dealer-item"
              key={item.dealer_id}
              onClick={() => this.toggleCheck(item)}
            >
              <div className="dealer-check">
                <Checkbox checked={value.indexOf(item.dealer_id) !== -1} />
              </div>
              <div className="dealer-info">
                <p className="dealer-name">
                  {item.dealer_type ? <>[{item.dealer_type}]&nbsp;</> : null}
                  {item.dealer_name}
                  <span className="dealer-scope">{item.sale_region}</span>
                </p>
                <p className="dealer-price">
                  经销商报价：<span>{item.price}</span>
                </p>
                <p className="dealer-location">
                  <span className="icon-location-ic" />
                  {item.address}
                </p>
                {item.dealer_phone ? (
                  <p className="dealer-phone">
                    经销商电话：<span>{item.dealer_phone}</span>
                  </p>
                ) : null}
              </div>
            </div>
          ))
        ) : (
          <p className="dealer-nodata icon-warn">当前城市暂无经销商报价</p>
        )}

        <style jsx>
          {`
            .dealer-item {
              padding-top: 20px;
              cursor: pointer;
            }

            .dealer-nodata {
              margin: 10px 0;
              color: #999;
              font-size: 16px;
              text-align: center;
              width: 100%;
            }

            .dealer-check {
              width: 48px;
              text-align: right;
              float: left;
            }

            .dealer-info {
              margin-left: 68px;
              padding-bottom: 20px;
              border-bottom: dashed 1px #e6e6e6;
            }

            .dealer-name {
              font-size: 16px;
            }

            .dealer-scope {
              font-size: 14px;
              color: #999;
              margin-left: 8px;
            }

            .dealer-price {
              margin-top: 12px;
              font-size: 14px;
              color: #999;
            }

            .dealer-price > span {
              color: #ff9100;
              font-size: 18px;
              font-weight: 500;
            }

            .dealer-location {
              margin-top: 14px;
              font-size: 14px;
              color: #666;
              padding-left: 20px;
              position: relative;
            }

            .dealer-location span {
              font-size: 16px;
              position: absolute;
              left: 0;
              top: 50%;
              margin-top: -8px;
            }

            .dealer-phone {
              margin-top: 12px;
              color: #999;
              font-size: 14px;
            }

            .dealer-phone > span {
              color: #ff9100;
              font-weight: 500;
              font-size: 16px;
            }

            .diffent-place-tips {
              padding: 8px 16px;
              font-size: 13px;
              text-align: center;
              color: #ff9100;
              background: #fffad6;
            }
          `}
        </style>
      </>
    )
  }

  render() {
    const { error } = this.props
    const { sortTypeIndex, loading } = this.state
    const sortTypeLastIndex = this.sortTypeList.length - 1
    return (
      <div className="dealer">
        <div className="dealer-header">
          经销商
          <span className="dealer-tip">
            <span className={error ? 'dealer-error icon-warn' : ''}>
              选择经销商（最多5家）
            </span>
          </span>
          <div className="dealer-filters">
            {this.sortTypeList.map((item, index) => (
              <Fragment key={index}>
                <a
                  className={index === sortTypeIndex ? 'active' : ''}
                  onClick={() => this.onChangeSortType(index)}
                >
                  {item}
                </a>
                {index !== sortTypeLastIndex && <span className="divider" />}
              </Fragment>
            ))}
          </div>
        </div>

        {loading ? <Loading /> : this._renderDealerList()}

        <style jsx>
          {`
            .dealer {
              width: 568px;
              margin-left: 99px;
            }

            .dealer-header {
              font-size: 16px;
              padding-bottom: 12px;
              border-bottom: solid 1px #e6e6e6;
            }

            .dealer-tip {
              font-size: 14px;
              color: #999;
              margin-left: 20px;
            }

            .divider {
              height: 100%;
              display: inline-block;
              width: 1px;
              height: 0.9em;
              margin: 0 12px;
              vertical-align: middle;
              background: #e6e6e6;
            }

            .dealer-filters {
              float: right;
            }

            .dealer-filters .active {
              font-weight: 500;
              color: #ff9100;
            }

            .dealer-error {
              font-size: 14px;
              color: #fa5555;
              line-height: 36px;
            }

            .icon-warn::before {
              content: '!';
              display: inline-block;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              font-size: 12px;
              line-height: 18px;
              font-weight: 500;
              border: 1px solid;
              text-align: center;
              margin-right: 4px;
            }

            .dealer-error::before {
              margin-right: 8px;
            }
          `}
        </style>
      </div>
    )
  }
}
